import React from "react";
import { MapPin, Clock, Phone, Mail, ArrowUpRight } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Reveal, Overline } from "./Reveal";
import { SERVICES } from "../lib/data";

const FORM_ACTION = "https://formsubmit.co/support@vynfive.com";

export default function Contact() {
  const contactNext = `${window.location.origin}/?thank-you=1`;
  const info = [
    { icon: MapPin, label: "Office", value: "VynFive Techlabs LLP" },
    { icon: Clock, label: "Business Hours", value: "Mon–Fri · 9:00–18:00 · 24×7 Support" },
    { icon: Mail, label: "Email", value: "support@vynfive.com " },
    { icon: Phone, label: "Phone", value: "+91 97246 86888" },
  ];

  return (
    <section id="contact" data-testid="contact" className="relative py-12 md:py-16 border-t border-line">
      <div className="max-w-[90rem] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{"Let's build"}</Overline>
            <h2 className="mt-5 font-heading text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              Book a <span className="text-gradient">consultation</span>
            </h2>
            <p className="mt-6 text-textsub leading-relaxed max-w-md">
              Tell us about your challenge. A senior engineer — not a salesperson — will get back to you within 24 hours.
            </p>

            <div className="mt-8 space-y-4">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4">
                  <span className="grid place-items-center h-11 w-11 rounded-xl glass text-brand shrink-0">
                    <it.icon size={18} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-textsub">{it.label}</p>
                    <p className="font-medium">{it.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="https://calendly.com" target="_blank" rel="noreferrer" data-testid="calendly-link" className="inline-flex items-center gap-1.5 rounded-full glass px-5 py-2.5 text-sm font-semibold hover:border-brand transition-colors">
                Schedule via Calendly <ArrowUpRight size={14} />
              </a>
              <a href="https://wa.me/9724686888" target="_blank" rel="noreferrer" data-testid="whatsapp-link" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] text-black px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity">
                WhatsApp us
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form action={FORM_ACTION} method="POST" data-testid="contact-form" className="rounded-2xl glass p-6 md:p-8 space-y-4">
              <input type="hidden" name="_subject" value="New consultation request from VynFive website" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_next" value={contactNext} />

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-textsub mb-2 block">Full name</label>
                  <Input name="name" data-testid="contact-name" placeholder="Jane Doe" className="bg-transparent border-line h-12" required />
                </div>
                <div>
                  <label className="text-sm text-textsub mb-2 block">Work email</label>
                  <Input name="email" data-testid="contact-email" type="email" placeholder="jane@company.com" className="bg-transparent border-line h-12" required />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-textsub mb-2 block">Company</label>
                  <Input name="company" data-testid="contact-company" placeholder="Acme Inc." className="bg-transparent border-line h-12" />
                </div>
                <div>
                  <label className="text-sm text-textsub mb-2 block">Service of interest</label>
                  <Select name="service" data-testid="contact-service">
                    <SelectTrigger className="bg-transparent border-line h-12">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm text-textsub mb-2 block">How can we help?</label>
                <Textarea name="message" data-testid="contact-message" placeholder="Tell us about your project..." rows={5} className="bg-transparent border-line resize-none" required />
              </div>
              <Button type="submit" data-testid="contact-submit" className="w-full h-12 rounded-full bg-brand text-[#05050A] font-semibold hover:opacity-90 glow-brand text-base">
                Send message
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
